import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import {
  PagesAmountOfPagesDocument,
  PagesFolderPagesListDocument,
  PagesFoldersListDocument,
  PagesUploadFilesBulkPublishDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { DeletePageDialog } from 'modules/pages/components/DeletePageDialog'
import { type ImportedPage } from 'modules/pages/components/UploadFilesModal/components/Upload'

import { PagePreview } from './components/PagePreview'
import { PageSidebar } from './components/PageSidebar'
import { PagesNavigation } from './components/PagesNavigation'

export interface PreviewHandle {
  isLastPage: boolean
  canProceed: boolean
  isPublishing: boolean
  handleAction: () => void
}

type PreviewState = {
  isLastPage: boolean
  canProceed: boolean
  isPublishing: boolean
}

interface PreviewProps {
  onCloseDirectly: () => void
  onPublish: () => void
  importedPages: ImportedPage[]
  folderType?: string
  onStateChange: (state: PreviewState) => void
}

export const Preview = forwardRef<PreviewHandle, PreviewProps>(function Preview(
  { onCloseDirectly, onPublish, importedPages: initialPages, folderType, onStateChange },
  ref
) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [pages, setPages] = useState(initialPages)
  const [pageToDelete, setPageToDelete] = useState<number | null>(null)
  const [visitedPageIndices, setVisitedPageIndices] = useState<Set<number>>(() => new Set([0]))
  const [containerHeight, setContainerHeight] = useState<number | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const handlePublishRef = useRef<() => Promise<void>>(() => Promise.resolve())
  // Initialize all page categories as null (not selected)
  const [pageCategories, setPageCategories] = useState<Map<number, string | null>>(
    () => new Map(initialPages.map((page) => [page.id, folderType || null]))
  )

  const currentPageId = pages[currentIndex]?.id
  const isLastPage = currentIndex === pages.length - 1
  const hasNextPage = currentIndex < pages.length - 1
  const currentCategory = currentPageId ? pageCategories.get(currentPageId) ?? null : null
  const hasCategorySelected = currentCategory !== null
  const allPagesHaveCategory = Array.from(pageCategories.values()).every(
    (category) => category !== null
  )

  useEffect(() => {
    track(trackingEvents.pages.PagesDocumentsReviewed, {
      documents_reviewed_count: initialPages.length,
    })
  }, [initialPages.length])

  useEffect(() => {
    if (containerRef.current?.parentElement?.parentElement) {
      const grandparentHeight = containerRef.current.parentElement.parentElement.clientHeight
      setContainerHeight(grandparentHeight)
    }
  }, [])

  const [bulkPublishPages, { loading: isPublishing }] = useMutation(
    PagesUploadFilesBulkPublishDocument,
    {
      extractorFn: (data) => data.handbookPagesMutations.bulkPublishHandbookpagesPage,
      refetchQueries: [
        PagesAmountOfPagesDocument,
        PagesFoldersListDocument,
        PagesFolderPagesListDocument,
      ],
    }
  )

  const handlePublish = async () => {
    try {
      track(trackingEvents.pages.PagesDocumentsPublished, {
        documents_published_count: pages.length,
      })
      const pageIds = pages.map((page) => page.id)
      const result = await bulkPublishPages({ variables: { ids: pageIds } })

      const handler = handleResult(result, () => ({}))

      if (handler?.success) {
        const message =
          pages.length === 1
            ? i18n.t('pages.upload_files_modal.preview.publish_success_singular')
            : i18n.t('pages.upload_files_modal.preview.publish_success_plural', {
                count: pages.length,
              })

        app.setFlash({
          type: 'correct',
          message,
        })
        track(trackingEvents.pages.PagesDocumentsPublishConfirmed, {
          documents_published_count: pages.length,
        })
        onPublish()
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('pages.upload_files_modal.preview.publish_error'),
        })
      }
    } catch (error) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('pages.upload_files_modal.preview.publish_error_generic'),
      })
    }
  }

  handlePublishRef.current = handlePublish

  const handleItemClick = useCallback(
    (id: string) => {
      const index = pages.findIndex((page) => page.id.toString() === id)
      // Only allow clicking on pages that have been visited
      if (index !== -1 && visitedPageIndices.has(index)) {
        setCurrentIndex(index)
      }
    },
    [pages, visitedPageIndices]
  )

  const handleNext = useCallback(() => {
    if (hasNextPage && hasCategorySelected) {
      const nextIndex = currentIndex + 1
      setCurrentIndex(nextIndex)
      setVisitedPageIndices((prev) => new Set(prev).add(nextIndex))
    }
  }, [hasNextPage, hasCategorySelected, currentIndex])

  const handleCategoryChange = useCallback((pageId: number, category: string) => {
    setPageCategories((prev) => new Map(prev).set(pageId, category))
  }, [])

  const handleDeletePage = useCallback((pageId: number) => {
    setPageToDelete(pageId)
  }, [])

  const handleDeleteSuccess = () => {
    if (!pageToDelete) return

    const deletedIndex = pages.findIndex((p) => p.id === pageToDelete)
    const newPages = pages.filter((page) => page.id !== pageToDelete)

    if (newPages.length === 0) {
      // Close directly without confirmation dialog
      onCloseDirectly()
      return
    }

    setPages(newPages)

    // Remove category for deleted page
    setPageCategories((prev) => {
      const newCategories = new Map(prev)
      newCategories.delete(pageToDelete)
      return newCategories
    })

    // Update visited indices after deletion
    setVisitedPageIndices((prev) => {
      const updatedVisitedIndices = new Set<number>()
      prev.forEach((index) => {
        if (index < deletedIndex) {
          updatedVisitedIndices.add(index)
        } else if (index > deletedIndex) {
          updatedVisitedIndices.add(index - 1)
        }
      })
      return updatedVisitedIndices
    })

    if (currentIndex >= newPages.length) {
      setCurrentIndex(newPages.length - 1)
    }

    setPageToDelete(null)
  }

  useImperativeHandle(
    ref,
    () => ({
      isLastPage,
      canProceed: isLastPage ? allPagesHaveCategory : hasNextPage && hasCategorySelected,
      isPublishing,
      handleAction: () => {
        if (isLastPage) {
          void handlePublishRef.current()
        } else {
          handleNext()
        }
      },
    }),
    [isLastPage, allPagesHaveCategory, hasNextPage, hasCategorySelected, isPublishing, handleNext]
  )

  useEffect(() => {
    onStateChange({
      isLastPage,
      canProceed: isLastPage ? allPagesHaveCategory : hasNextPage && hasCategorySelected,
      isPublishing,
    })
  }, [
    isLastPage,
    allPagesHaveCategory,
    hasNextPage,
    hasCategorySelected,
    isPublishing,
    onStateChange,
  ])

  return (
    <>
      <Box ref={containerRef} width="full" flexGrow flexDirection="row" overflowY="hidden">
        <PagesNavigation
          pages={pages}
          activeItem={currentPageId?.toString()}
          onItemClick={handleItemClick}
          onDeletePage={handleDeletePage}
          visitedPageIndices={visitedPageIndices}
        />
        <Box flexGrow overflowY="hidden">
          {currentPageId && (
            <PagePreview pageId={currentPageId} containerHeight={containerHeight ?? undefined} />
          )}
        </Box>
        {currentPageId && (
          <PageSidebar
            pageId={currentPageId}
            category={currentCategory}
            setCategory={(category) => handleCategoryChange(currentPageId, category)}
          />
        )}
      </Box>
      {pageToDelete && (
        <DeletePageDialog
          pageId={pageToDelete}
          onClose={() => setPageToDelete(null)}
          onDeleteSuccess={handleDeleteSuccess}
        />
      )}
    </>
  )
})
